import { z } from 'zod';
import { openWorldRatingSchema } from './content';
import { openWorldDirectionSchema } from './direction';
import {
  OPEN_WORLD_CONTENT_VERSION,
  OPEN_WORLD_GRID_VERSION,
  openWorldBoundsSchema,
  openWorldCoordinateSchema,
} from './grid';
import { OPEN_WORLD_SAFE_PORT_IDS } from './ports';

/**
 * The PUBLIC face of the open world.
 *
 * ## The line this file draws
 *
 * Everything here may be sent to a browser. Everything absent from here may
 * not. In particular there is no seed, no derivation label, no local position,
 * no space id, no count of bosses, comets or planets, and no reward of any
 * kind: the catalogue exists on the server, and what a client learns about a
 * quadrant it learns by going there in a later gate.
 *
 * That is why {@link openWorldQuadrantPublicSchema} carries `danger` and
 * `resources` as NULLABLE. Null is the honest answer for a quadrant nobody has
 * discovered, and shipping the whole grid's ratings would hand a player the map
 * the game is supposed to be about uncovering.
 *
 * ## Why the limits are a contract at all
 *
 * A consumer has to know where the world ends to stop drawing an arrow at the
 * edge, and it must not learn that from a constant compiled into its own
 * bundle: the server owns the bounds, a world stores the bounds it was made
 * with, and a client that assumed ten would be wrong the day one is fifteen.
 */

/**
 * The active world's shape and versions, as the server states them.
 *
 * No world id: which world a session belongs to is a property of the session,
 * and a client that could name a world could ask about a retired one.
 */
export const openWorldLimitsSchema = z
  .object({
    bounds: openWorldBoundsSchema,
    gridVersion: z.number().int().min(1),
    contentVersion: z.number().int().min(1),
  })
  .strict();

export type OpenWorldLimits = z.infer<typeof openWorldLimitsSchema>;

/** The limits this build was compiled against. The server still states its own. */
export const OPEN_WORLD_LOCAL_VERSIONS = Object.freeze({
  gridVersion: OPEN_WORLD_GRID_VERSION,
  contentVersion: OPEN_WORLD_CONTENT_VERSION,
});

/**
 * One quadrant, as a client may know it.
 *
 * `label` travels alongside the indices as a convenience for display, and the
 * indices remain the identity: nothing matches on the label, and a client that
 * disagreed about it would still address the right quadrant.
 */
export const openWorldQuadrantPublicSchema = z
  .object({
    coordinate: openWorldCoordinateSchema,
    /** `F/10`. Presentation, computed by the server from the indices. */
    label: z.string().regex(/^[A-Z]\/[1-9][0-9]*$/),
    /** The port id when this quadrant is one of the eight, else null. */
    safePortId: z.enum(OPEN_WORLD_SAFE_PORT_IDS).nullable(),
    /**
     * 1..5, or null while undiscovered. A safe port reports `0` for neither -
     * it reports `safePortId` and a null rating, because "no danger here" and
     * "you have not been here" are different sentences.
     */
    danger: openWorldRatingSchema.nullable(),
    resources: openWorldRatingSchema.nullable(),
  })
  .strict();

export type OpenWorldQuadrantPublic = z.infer<typeof openWorldQuadrantPublicSchema>;

/**
 * A neighbour, as a client may know it: a direction and where it leads.
 *
 * `coordinate` is null at the edge of the world. Sending the direction with a
 * null target rather than omitting the entry is deliberate - a consumer drawing
 * four arrows needs to know the fourth exists and leads nowhere.
 */
export const openWorldNeighbourPublicSchema = z
  .object({
    direction: openWorldDirectionSchema,
    coordinate: openWorldCoordinateSchema.nullable(),
    label: z
      .string()
      .regex(/^[A-Z]\/[1-9][0-9]*$/)
      .nullable(),
  })
  .strict();

export type OpenWorldNeighbourPublic = z.infer<typeof openWorldNeighbourPublicSchema>;
